// JOBE-0160.4: W1 Trend panel — three deviation-mode sparkline cards.
//
// Three 14-day sparkline cards (eval pass rate, task throughput, latency p50)
// using <Spark mode="deviation" />. D11: deviation mode centers on window mean
// so the shape of the period is the story, not absolute zero-baseline bars.
//
// Tone per metric reflects whether the direction is *favorable*:
//   eval-pass-rate  → higher better: up=ok, down=bad
//   task-throughput → higher better: up=ok, down=warn
//   latency-p50     → lower better:  up=bad, down=ok
//
// Cards are informational only (no drill target in v1).
//
// Data source — 2026-05-24:
//   When wcc.mock.trend is ON, the fixture is rendered as-is.
//   When OFF (default), there is no time-series endpoint on the wire, so
//   the three cards render as placeholders: label + unit intact, value '—',
//   spark omitted, and a one-line caption signalling "endpoint pending".
//   The layout is preserved so the page composition matches design.

import { flaggedFetch } from '../lib/flags.js';
import { useFlagStore } from '../lib/useFlagStore.js';
import { Spark } from '../components/Spark/Spark.jsx';
import './TrendPanel.css';

const EM_DASH = '—';

// Static placeholder definitions used when no real time-series exists.
// Labels/units mirror the fixture so the page layout is identical.
const PLACEHOLDER_METRICS = [
  { id: 'eval-pass-rate',  label: 'Eval pass rate',  unit: '%' },
  { id: 'task-throughput', label: 'Task throughput', unit: 'tasks/day' },
  { id: 'latency-p50',     label: 'Latency p50',     unit: 'ms' },
];

// Tone computation: compare last two points to determine direction.
function computeTone(metricId, points) {
  if (!points || points.length < 2) return 'neutral';
  const trendingUp = points[points.length - 1] > points[points.length - 2];

  switch (metricId) {
    case 'eval-pass-rate':
      return trendingUp ? 'ok' : 'bad';
    case 'task-throughput':
      return trendingUp ? 'ok' : 'warn';
    case 'latency-p50':
      return trendingUp ? 'bad' : 'ok';
    default:
      return 'neutral';
  }
}

// Value formatting per unit type.
function formatValue(value, unit) {
  if (unit === '%') return `${(value * 100).toFixed(1)}%`;
  if (unit === 'ms') return `${Math.round(value)}ms`;
  // tasks/day or raw count
  return `${Math.round(value)}`;
}

function TrendCard({ metric }) {
  const { id, label, unit, points, mean } = metric;
  const tone = computeTone(id, points);
  const latestValue = points[points.length - 1];
  const delta = latestValue - mean;
  const deltaStr = delta >= 0
    ? `+${formatValue(delta, unit)}`
    : `-${formatValue(Math.abs(delta), unit)}`;

  // Map 'neutral' to 'ink' since Spark doesn't support 'neutral' tone.
  const sparkTone = tone === 'neutral' ? 'ink' : tone;

  return (
    <div className="trend-card">
      <h3 className="trend-title">{label}</h3>
      <span className="trend-unit">{unit}</span>
      <Spark
        mode="deviation"
        data={points}
        tone={sparkTone}
        unit={unit}
        mean={mean}
        ariaLabel={`${label} trend over 14 days`}
      />
      <footer className="trend-footer">
        <span className="trend-value">{formatValue(latestValue, unit)}</span>
        <span className="trend-delta" data-tone={tone}>
          {deltaStr} vs avg
        </span>
      </footer>
    </div>
  );
}

// Placeholder card — no spark, em-dash value, italic caption noting that the
// time-series endpoint hasn't shipped yet. Preserves the W1 page layout.
function TrendPlaceholderCard({ metric }) {
  const { label, unit } = metric;
  return (
    <div className="trend-card trend-card--placeholder">
      <h3 className="trend-title">{label}</h3>
      <span className="trend-unit">{unit}</span>
      <footer className="trend-footer">
        <span className="trend-value">{EM_DASH}</span>
        <span
          className="trend-delta"
          style={{ fontStyle: 'italic' }}
          aria-label={`${label} — no time-series yet`}
        >
          no time-series yet
        </span>
      </footer>
    </div>
  );
}

export function TrendPanel() {
  const overrides = useFlagStore((s) => s.overrides);
  const trends = flaggedFetch('wcc.mock.trend', overrides);

  // Mock-flag ON → render fixture sparks. OFF → render the three labelled
  // placeholders so the page layout stays put.
  if (Array.isArray(trends) && trends.length > 0) {
    return (
      <div className="trend-panel">
        {trends.map((metric) => (
          <TrendCard key={metric.id} metric={metric} />
        ))}
      </div>
    );
  }

  return (
    <div className="trend-panel">
      {PLACEHOLDER_METRICS.map((metric) => (
        <TrendPlaceholderCard key={metric.id} metric={metric} />
      ))}
    </div>
  );
}
